// frontend/src/contexts/LabContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchRandomInstructionId, fetchLabContent, fetchInstructionAndPrimeLLM, submitTerminalCommand as apiSubmitTerminalCommand, fetchExplanation, pythonApi, fetchQCMMaxTime } from '../services/api';
import { useTranslation } from 'react-i18next';

const LabContext = createContext();

export const useLab = () => {
    return useContext(LabContext);
};

export const LabProvider = ({ children }) => {
    const { t } = useTranslation();
    const [labData, setLabData] = useState(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const terminalWriterRef = useRef(null);
    const [options, setOptions] = useState([]);
    const [displayMode, setDisplayMode] = useState('instruction');
    const [qcmSolutionContent, setQcmSolutionContent] = useState(null);
    const [wrongAnswerCount, setWrongAnswerCount] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [attemptCount, setAttemptCount] = useState(1);
    const [isSimilarTask, setIsSimilarTask] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);

    const [showNavigationButton, setShowNavigationButton] = useState(false);
    const [navigationButtonLabel, setNavigationButtonLabel] = useState('');
    const [navigationButtonAction, setNavigationButtonAction] = useState(null);

    const currentSection = labData?.sub_instructions?.[currentSectionIndex];

    const goToNextSectionRef = useRef(null);
    const handleOptionRef = useRef(null);
    const loadSpecificInstructionRef = useRef(null);
    const handleSimilarTaskRef = useRef(null);

    const handleTimeUp = useCallback(() => {
        setIsTimerRunning(false);
        // Silently show solution when time is up, without feedback message
        handleOption('solution');
    }, []);

    const loadSpecificInstruction = useCallback(async (instructionId) => {
        setLoading(true);
        setError(null);
        try {
            const fullInstructionData = await fetchLabContent(instructionId);
            if (!fullInstructionData || !fullInstructionData.sub_instructions || fullInstructionData.sub_instructions.length === 0) {
                throw new Error(t('error_fetching_sub_instructions'));
            }
            setLabData(fullInstructionData);
            setCurrentSectionIndex(0);
            setError(null);
            setDisplayMode('instruction');
            setQcmSolutionContent(null);
            setWrongAnswerCount(0);
            setShowNavigationButton(false);
            setNavigationButtonLabel('');
            setNavigationButtonAction(null);
            setAttemptCount(1);
            setIsSimilarTask(false);
            // RG3-1: Initialisation du timer pour nouvelle instruction
            elapsedTimeRef.current = 0;
            if (timerRef.current) {
                timerRef.current.reset(0);
            }

            // Timer logic for the first sub-instruction
            const firstSub = fullInstructionData.sub_instructions[0];
            if (firstSub && firstSub.metadata && firstSub.metadata.execution_time > 0) {
                setMaxTime(firstSub.metadata.execution_time * 60);
                setIsTimerRunning(true);
            } else {
                setMaxTime(0);
                setIsTimerRunning(false);
            }

            if (handleOptionRef.current) {
                setOptions([
                    { id: 'qcm', label: t('option_qcm'), action: () => handleOptionRef.current('qcm') },
                    { id: 'solution', label: t('option_solution'), action: () => handleOptionRef.current('solution') },
                    { id: 'help', label: t('option_help'), action: () => handleOptionRef.current('help') }
                ]);
            }
            const initialSubInstructionId = fullInstructionData.sub_instructions[0].sub_instruction_id;
            fetchInstructionAndPrimeLLM(instructionId, initialSubInstructionId).catch(err => console.error("Priming error:", err));
            return true;
        } catch (err) {
            setError(t('lab_load_error') + err.message);
            return false;
        } finally {
            setLoading(false);
        }
    }, [t]);

    const goToNextSection = useCallback(async () => {
        setFeedback(null);
        setOptions([]);
        setDisplayMode('instruction');
        setQcmSolutionContent(null);
        setWrongAnswerCount(0);
        setShowNavigationButton(false);
        setAttemptCount(1);
        setIsSimilarTask(false);
        // RG3-1: Initialisation du timer SEULEMENT pour nouvelle sous-instruction
        elapsedTimeRef.current = 0;
        if (timerRef.current) {
            timerRef.current.reset(0);
        }

        if (labData && currentSectionIndex < labData.sub_instructions.length - 1) {
            const nextIndex = currentSectionIndex + 1;
            setCurrentSectionIndex(nextIndex);
            const nextSection = labData.sub_instructions[nextIndex];

            // Timer logic for the next sub-instruction
            if (nextSection && nextSection.metadata && nextSection.metadata.execution_time > 0) {
                setMaxTime(nextSection.metadata.execution_time * 60);
                setIsTimerRunning(true);
            } else {
                setMaxTime(0);
                setIsTimerRunning(false);
            }
        } else {
            setLoading(true);
            try {
                const randomId = await fetchRandomInstructionId();
                if (randomId) {
                    await loadSpecificInstructionRef.current(randomId);
                } else {
                    setFeedback({ type: 'success', message: t('congratulations_lab_complete') });
                }
            } catch (err) {
                setError(t('error_loading_next_instruction') + err.message);
            } finally {
                setLoading(false);
            }
        }
    }, [labData, currentSectionIndex, t]);

    const handleSimilarTask = useCallback(() => {
        if (!currentSection || !currentSection.similar_sub_instruction) return;

        const similarTaskData = currentSection.similar_sub_instruction;

        // Create a new labData object with the modified sub-instruction
        const newLabData = {
            ...labData,
            sub_instructions: labData.sub_instructions.map((sub, index) =>
                index === currentSectionIndex ? similarTaskData : sub
            )
        };

        setLabData(newLabData);
        setIsSimilarTask(true);
        setFeedback(null);
        setDisplayMode('instruction');
        setAttemptCount(1);
        // RG3-1: Initialisation du timer pour tâche similaire
        elapsedTimeRef.current = 0;
        if (timerRef.current) {
            timerRef.current.reset(0);
        }

        // Timer logic for the similar task
        if (similarTaskData.metadata && similarTaskData.metadata.execution_time > 0) {
            setMaxTime(similarTaskData.metadata.execution_time * 60);
            setIsTimerRunning(true);
        } else {
            setMaxTime(0);
            setIsTimerRunning(false);
        }

    }, [currentSection, labData, currentSectionIndex]);

    const handleOption = useCallback((optionType) => {
        if (!currentSection) return;

        setFeedback(null);

        switch (optionType) {
            case 'qcm':
                setDisplayMode('qcm_view');
                // RG3-1: Réinitialisation du timer pour un nouveau QCM
                elapsedTimeRef.current = 0;
                if (timerRef.current) {
                    timerRef.current.reset(0);
                }
                // Fetch QCM max time and start timer
                fetchQCMMaxTime().then(time => {
                    setMaxTime(time);
                    setIsTimerRunning(true);
                });
                setAttemptCount(1);
                break;
            case 'solution':
                // RG3-11: Arrêt du timer quand la solution est demandée
                // MAIS le temps écoulé reste affiché (pas de reset à 0)
                setIsTimerRunning(false);
                setDisplayMode('solution_view');
                // Implement RG4-3: Show solution on request
                // Ensure navigation buttons are shown as per RG5-1
                updateNavigationButtons();
                // Force options update for solution view to ensure buttons are shown
                const newOptions = [];
                if (!isSimilarTask && currentSection?.similar_sub_instruction) {
                    newOptions.push({ id: 'similar_task', label: t('make_similar_task'), action: () => handleSimilarTaskRef.current() });
                }
                if (labData && currentSectionIndex < labData.sub_instructions.length - 1) {
                    newOptions.push({ id: 'next_task', label: t('next_task'), action: () => goToNextSectionRef.current() });
                } else {
                    newOptions.push({ id: 'next_instruction', label: t('next_instruction'), action: () => goToNextSectionRef.current() });
                }
                setOptions(newOptions);
                break;
            case 'help':
                const helpContent = currentSection.help || t('help_coming_soon');
                setFeedback({ type: 'info', message: helpContent });
                break;
            case 'try_again':
                setDisplayMode('instruction');
                setFeedback(null);
                // RG3-11: PAS de réinitialisation du temps écoulé
                // Le timer continue avec le temps déjà écoulé
                if (currentSection && currentSection.metadata && currentSection.metadata.execution_time > 0) {
                    setMaxTime(currentSection.metadata.execution_time * 60);
                    if (!isTimerRunning) {
                        setIsTimerRunning(true);
                    }
                } else {
                    setMaxTime(0);
                    setIsTimerRunning(false);
                }
                setAttemptCount(1);
                // PAS de reset du temps écoulé
                break;
            default:
                break;
        }
    }, [currentSection, t, labData, currentSectionIndex]);

    useEffect(() => {
        handleOptionRef.current = handleOption;
        goToNextSectionRef.current = goToNextSection;
        loadSpecificInstructionRef.current = loadSpecificInstruction;
        handleSimilarTaskRef.current = handleSimilarTask;
    }, [handleOption, goToNextSection, loadSpecificInstruction, handleSimilarTask]);

    useEffect(() => {
        if (displayMode === 'instruction' && !feedback) {
            setOptions([
                { id: 'qcm', label: t('option_qcm'), action: () => handleOptionRef.current('qcm') },
                { id: 'solution', label: t('option_solution'), action: () => handleOptionRef.current('solution') },
                { id: 'help', label: t('option_help'), action: () => handleOptionRef.current('help') }
            ]);
        } else if (feedback && feedback.type === 'error') {
            setOptions([
                { id: 'qcm', label: t('option_qcm'), action: () => handleOptionRef.current('qcm') },
                { id: 'solution', label: t('option_solution'), action: () => handleOptionRef.current('solution') },
                { id: 'help', label: t('option_help'), action: () => handleOptionRef.current('help') }
            ]);
        } else if (feedback && feedback.type === 'success') {
            // Do nothing, options are set manually in the success handlers
        } else if (displayMode === 'solution_view') {
            // Do nothing, options are set manually in handleOption for solution_view
        } else {
            setOptions([]);
        }
    }, [displayMode, feedback, t]);


    useEffect(() => {
        const loadInitialInstruction = async () => {
            try {
                const randomId = await fetchRandomInstructionId();
                if (randomId && loadSpecificInstructionRef.current) {
                    await loadSpecificInstructionRef.current(randomId);
                } else {
                    throw new Error("Failed to fetch a random instruction ID.");
                }
            } catch (err) {
                setError(t('error_loading_initial_instruction') + err.message);
                setLoading(false);
            }
        };

        loadInitialInstruction();
    }, []);

    // Use a ref to store elapsed time and timer instance
    const elapsedTimeRef = useRef(0);
    const timerRef = useRef(null);

    // Timer complètement isolé - ne déclenche AUCUN re-render
    class Timer {
        constructor(maxTime, onTimeUp) {
            this.maxTime = maxTime;
            this.elapsed = 0;
            this.isRunning = false;
            this.interval = null;
            this.onTimeUp = onTimeUp;
        }

        start() {
            if (!this.isRunning) {
                this.isRunning = true;
                this.interval = setInterval(() => {
                    this.elapsed += 1;
                    elapsedTimeRef.current = this.elapsed;
                    // Plus de console.log ni de mise à jour UI
                    if (this.maxTime > 0 && this.elapsed >= this.maxTime) {
                        this.stop();
                        if (this.onTimeUp) this.onTimeUp();
                    }
                }, 1000);
            }
        }

        stop() {
            if (this.isRunning) {
                this.isRunning = false;
                if (this.interval) clearInterval(this.interval);
                elapsedTimeRef.current = this.elapsed;
            }
        }

        reset(maxTime = this.maxTime) {
            this.stop();
            this.elapsed = 0;
            elapsedTimeRef.current = 0;
            this.maxTime = maxTime;
        }

        getElapsedTime() {
            return this.elapsed;
        }
    }

    // Initialize timer isolé - sans setElapsedTime
    useEffect(() => {
        timerRef.current = new Timer(maxTime, handleTimeUp);
        if (isTimerRunning) {
            timerRef.current.start();
        }
        return () => {
            if (timerRef.current) {
                timerRef.current.stop();
            }
        };
    }, []);

    // Sync timer state with UI events
    useEffect(() => {
        if (timerRef.current) {
            if (isTimerRunning) {
                timerRef.current.start();
            } else {
                timerRef.current.stop();
            }
        }
    }, [isTimerRunning]);

    // Update maxTime when it changes
    useEffect(() => {
        if (timerRef.current) {
            timerRef.current.maxTime = maxTime;
        }
    }, [maxTime]);

    const handleQCMSubmission = useCallback((isCorrect, qcmDetails = null) => {
        if (isCorrect) {
            // RG3-21: Arrêt du timer SEULEMENT quand la bonne réponse est donnée
            setIsTimerRunning(false);
            setFeedback({ type: 'success', message: t('qcm_correct_answer') });
            setDisplayMode('solution_view');
            setQcmSolutionContent(qcmDetails);

            // Implement RG5: Show navigation buttons after correct answer
            updateNavigationButtons();
        } else {
            setWrongAnswerCount(prev => prev + 1);
            setAttemptCount(prev => prev + 1);
            // Convertir qcmDetails en chaîne si c'est un objet
            let errorMessage = t('qcm_incorrect_answer');
            if (qcmDetails && typeof qcmDetails === 'object') {
                // Si qcmDetails est un objet, utiliser une propriété spécifique ou le message par défaut
                errorMessage = qcmDetails.message || qcmDetails.explanation || t('qcm_incorrect_answer');
            } else if (qcmDetails && typeof qcmDetails === 'string') {
                errorMessage = qcmDetails;
                // Remplacer les messages d'erreur LLM par des messages plus positifs
                if (errorMessage.includes('Request Error calling LLM') || errorMessage.includes('All connection attempts failed')) {
                    errorMessage = t('llm_connection_error');
                }
            }
            setFeedback({ type: 'error', message: errorMessage });
        }
    }, [t, labData, currentSectionIndex, isSimilarTask, currentSection]);

    const submitTerminalResponse = useCallback(async (command, fileRef = null) => {
        if (!labData || !currentSection) return;

        const payload = {
            instruction_id: labData.instruction_id,
            sub_instruction_id: currentSection.sub_instruction_id,
            student_answer: command,
            expected_solution: currentSection.solution,
            sub_instruction_description: currentSection.sub_instruction_description,
            elapsed_time: elapsedTimeRef.current,
            nb_attempt: attemptCount,
            ...(fileRef && { file_reference: { name: fileRef.name, content: fileRef.content } })
        };

        try {
            // First API call for quick binary evaluation
            const binaryResponse = await apiSubmitTerminalCommand(payload);

            if (binaryResponse.success && binaryResponse.evaluation.is_correct === "yes") {
                // RG3-21: Arrêt du timer SEULEMENT quand la bonne réponse est donnée par l'étudiant
                setIsTimerRunning(false);
                setFeedback({ type: 'success', message: t('well_done') });
                setDisplayMode('solution_view');

                // Implement RG5: Show navigation buttons after correct answer
                updateNavigationButtons();
            } else {
                setAttemptCount(prev => prev + 1);
                // Affichage immédiat du feedback 'incorrect' avec placeholder (réponse RAPIDE reçue)
                let initialMessage = t('terminal_wrong_command_prefix');
                setFeedback({ type: 'error', message: `${initialMessage} ${t('explanation_coming_soon') || 'Explanation coming soon...'}` });

                // Deuxième appel SÉQUENTIEL pour l'explication détaillée (Role 2)
                fetchExplanation(payload)
                    .then(explanationResponse => {
                        if (explanationResponse.success && explanationResponse.evaluation.explanation) {
                            let explanation = explanationResponse.evaluation.explanation;
                            // Remplacer les messages d'erreur LLM par des messages plus positifs
                            if (explanation.includes('Request Error calling LLM') || explanation.includes('All connection attempts failed')) {
                                explanation = t('llm_connection_error');
                            }
                            const updatedFeedbackMessage = `${initialMessage} ${explanation}`;
                            setFeedback(prev => ({ ...prev, message: updatedFeedbackMessage }));
                        } else {
                            setFeedback(prev => ({ ...prev, message: `${initialMessage} ${t('llm_connection_error')}` }));
                        }
                    })
                    .catch(explanationError => {
                        setFeedback(prev => ({ ...prev, message: `${initialMessage} ${t('llm_connection_error')}` }));
                    });

                setWrongAnswerCount(prev => prev + 1);
            }
        } catch (err) {
            setFeedback({ type: "error", message: err.message });
        }
    }, [labData, currentSection, t, currentSectionIndex, isSimilarTask]);

    // Function to update navigation buttons based on RG5
    const updateNavigationButtons = useCallback(() => {
        const newOptions = [];
        if (!isSimilarTask && currentSection?.similar_sub_instruction) {
            newOptions.push({ id: 'similar_task', label: t('make_similar_task'), action: () => handleSimilarTaskRef.current() });
        }
        if (labData && currentSectionIndex < labData.sub_instructions.length - 1) {
            newOptions.push({ id: 'next_task', label: t('next_task'), action: () => goToNextSectionRef.current() });
        } else {
            newOptions.push({ id: 'next_instruction', label: t('next_instruction'), action: () => goToNextSectionRef.current() });
        }
        setOptions(newOptions);
    }, [labData, currentSectionIndex, isSimilarTask, currentSection, t]);

    const value = {
        labData, currentSection, loading, error, feedback, handleQCMSubmission,
        submitTerminalResponse, options, handleOption: handleOptionRef.current,
        displayMode, qcmSolutionContent,
        showNavigationButton, navigationButtonLabel, navigationButtonAction, wrongAnswerCount,
        setTerminalWriter: (writer) => { terminalWriterRef.current = writer; },
        maxTime, isTimerRunning, setIsTimerRunning, attemptCount, handleTimeUp, isSimilarTask,
        elapsedTime,
        getElapsedTime: () => {
            return elapsedTimeRef.current;
        }
    };

    return <LabContext.Provider value={value}>{children}</LabContext.Provider>;
};
